import { Icon } from "@iconify/react";
import { Drawer, Flex, Grid, Image, Layout, Menu, Badge } from "antd";
import { Link, useLocation } from "react-router";
import logo from "../assets/logo-siskop2mi.webp";
import pallete from "../utils/pallete";
import { sidebarStore } from "../store/sidebarStore";
import TrainingCatalogIcon from "@/assets/icons/trainingCatalogIcon.svg?react";
import TrainingCatalogEnableIcon from "@/assets/icons/trainingCatalogEnableIcon.svg?react";
import TestimonyIcon from "@/assets/icons/testimonyIcon.svg?react";
import TestimonyEnableIcon from "@/assets/icons/testimonyEnableIcon.svg?react";
import FAQIcon from "@/assets/icons/faqIcon.svg?react";
import FAQEnableIcon from "@/assets/icons/faqEnableIcon.svg?react";
import LogIcon from "@/assets/icons/logIcon.svg?react";
import LogEnableIcon from "@/assets/icons/logEnableIcon.svg?react";
import { useMe } from "../hooks/useMe";
import { useQuery } from "@tanstack/react-query";
import trainingCatalogService from "../services/training-catalog.service";
import { COMPLAINT_READER_POSITIONS } from "../utils/complaintAccess";

// eslint-disable-next-line react/prop-types
function SubmenuIcon({ collapse }) {
  return (
    <Icon
      icon="lucide:dot"
      style={
        collapse
          ? { marginBottom: -9, marginLeft: -10, marginRight: 0 }
          : { marginBottom: -1, marginLeft: -30, marginRight: 0 }
      }
      width="28"
      height="28"
    />
  );
}

const filterMenuItems = (menuItems, directorate, position) => {
  if (position?.toLowerCase() === "superadmin") {
    return menuItems;
  }
  return menuItems.filter(
    (item) =>
      item.role.includes(directorate) &&
      (!item.positions ||
        item.positions.includes((position || "").trim().toLowerCase())),
  );
};

const MENU_SECTIONS = [
  { key: "section-general", label: "Umum", items: ["dashboard"] },
  {
    key: "section-training",
    label: "Verifikasi Pelatihan",
    items: ["training-catalog", "training-verification"],
  },
  {
    key: "section-institution",
    label: "Verifikasi Lembaga",
    items: [
      "institutional-verification",
      "migran-center",
      "ministry-vocational-institution",
    ],
  },
  {
    key: "section-content",
    label: "Publikasi & Konten",
    items: ["announcement", "faq", "testimony", "help-center"],
  },
  {
    key: "section-administration",
    label: "Administrasi Sistem",
    items: ["user", "master-data", "setting", "history-log"],
  },
];

const sectionTitleStyle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: pallete.grey[500],
};

// Groups the (already permission-filtered) menu items into titled sections.
// Menus not listed in MENU_SECTIONS are kept in a trailing "Lainnya" section,
// and sections left empty after filtering are hidden. A collapsed sidebar has
// no room for titles, so sections are separated by dividers instead.
const groupMenuItems = (menuItems, collapsed) => {
  const sectionedKeys = new Set(MENU_SECTIONS.flatMap((e) => e.items));
  const sections = [
    ...MENU_SECTIONS.map((section) => ({
      ...section,
      children: section.items
        .map((key) => menuItems.find((item) => item.key === key))
        .filter(Boolean),
    })),
    {
      key: "section-other",
      label: "Lainnya",
      children: menuItems.filter((item) => !sectionedKeys.has(item.key)),
    },
  ].filter((section) => section.children.length > 0);

  if (collapsed) {
    return sections.flatMap((section, index) => [
      ...(index > 0
        ? [{ type: "divider", key: `${section.key}-divider` }]
        : []),
      ...section.children,
    ]);
  }

  return sections.map((section) => ({
    type: "group",
    key: section.key,
    label: <span style={sectionTitleStyle}>{section.label}</span>,
    children: section.children,
  }));
};

export default function Sidebar() {
  const location = useLocation();

  const breakpoint = Grid.useBreakpoint();

  const { data: userDataProfile } = useMe();
  const userDirectorate = userDataProfile?.data?.directorate;
  const userPosition = userDataProfile?.data?.position;

  const { data: statusCountsData } = useQuery({
    queryKey: ["training-catalog-status-counts"],
    queryFn: () => trainingCatalogService.fetchStatusCounts(),
    refetchInterval: 15000,
  });

  const { collapse, drawerCollapse, toggleDrawer } = sidebarStore(
    (state) => state,
  );

  const isTrainingCatalogActive =
    location.pathname.startsWith("/training-catalog");
  const isFaqActive = location.pathname.startsWith("/faq");
  const isAnnouncementActive = location.pathname.startsWith("/announcement");
  const isTestimonyActive = location.pathname.startsWith("/testimony");
  const isLogActive = location.pathname.startsWith("/history-log");

  const getSelectedKey = () => {
    if (location.pathname == "/") {
      return ["dashboard"];
    }
    return location.pathname.replace("/", "");
  };

  const getOpenKey = () => {
    if (location.pathname == "/") {
      return [];
    }
    if (location.pathname.includes("testimony")) {
      return ["testimony"];
    }
    if (location.pathname.includes("article")) {
      return ["article"];
    }
    if (location.pathname.includes("training-catalog")) {
      return ["training-catalog"];
    }
    if (location.pathname.includes("training-verification")) {
      return ["training-verification"];
    }
    if (location.pathname.includes("vocational-institution")) {
      return ["vocational-institution"];
    }
    if (location.pathname.includes("ministry-vocational-institution")) {
      return ["ministry-vocational-institution"];
    }
    if (location.pathname.includes("faq")) {
      return ["faq"];
    }
    if (location.pathname.includes("user")) {
      return ["user"];
    }
    if (location.pathname.includes("master-data")) {
      return ["master-data"];
    }
    if (location.pathname.includes("history-log")) {
      return ["history-log"];
    }

    return [];
  };

  const items = [
    {
      key: "dashboard",
      label: <Link to={"/"}>Dashboard</Link>,
      role: ["*", "Direktorat Vokasi", "Direktorat Peningkatan Kapasitas"],
      icon: (
        <Icon
          icon="duo-icons:dashboard"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="22"
          height="22"
        />
      ),
    },
    {
      key: "training-catalog",
      permission: "Katalog Pelatihan",
      label: "Katalog Pelatihan",
      role: ["Direktorat Peningkatan Kapasitas", "*"],
      icon: isTrainingCatalogActive ? (
        <TrainingCatalogEnableIcon
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ) : (
        <TrainingCatalogIcon
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
      children: [
        {
          key: "training-catalog/pending",
          label: (
            <Flex
              align="center"
              justify="space-between"
              style={{ width: "100%", gap: "8px" }}
            >
              <Link to={"/training-catalog/pending"}>Pengajuan Berjalan</Link>
            </Flex>
          ),
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "training-catalog/accepted",
          label: (
            <Flex
              align="center"
              justify="space-between"
              style={{ width: "100%", gap: "8px" }}
            >
              <Link to={"/training-catalog/accepted"}>Pengajuan Diterima</Link>
            </Flex>
          ),
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "training-catalog/rejected",
          label: (
            <Flex
              align="center"
              justify="space-between"
              style={{ width: "100%", gap: "8px" }}
            >
              <Link to={"/training-catalog/rejected"}>Pengajuan Ditolak</Link>
            </Flex>
          ),
          icon: <SubmenuIcon collapse={collapse} />,
        },
      ],
    },
    {
      key: "training-verification",
      permission: "Verifikasi Pelatihan",
      label: <Link to={"/training-verification"}>Verifikasi Pelatihan</Link>,
      role: ["Direktorat Peningkatan Kapasitas", "*"],
      icon: (
        <Icon
          icon="ic:round-verified"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "institutional-verification",
      permission: "Verifikasi Lembaga",
      label: <Link to={"/institutional-verification"}>Verifikasi Lembaga</Link>,
      role: ["Direktorat Vokasi", "*"],
      icon: (
        <Icon
          icon="solar:diploma-verified-bold"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "migran-center",
      permission: "Migran Center",
      label: <Link to={"/migran-center"}>Migrant Center</Link>,
      role: ["Direktorat Vokasi", "*"],
      icon: (
        <Icon
          icon="solar:buildings-3-bold-duotone"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "ministry-vocational-institution",
      permission: "Lembaga Vokasi Kementerian",
      label: (
        <Link to={"/ministry-vocational-institution"}>
          Lembaga Vokasi Kementerian
        </Link>
      ),
      role: ["Direktorat Vokasi", "*"],
      icon: (
        <Icon
          icon="solar:buildings-2-bold-duotone"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "help-center",
      permission: "Pusat Bantuan",
      label: <Link to={"/help-center"}>Pusat Bantuan</Link>,
      role: ["Direktorat Peningkatan Kapasitas", "*", "Direktorat Vokasi"],
      // Read-only messages from the website: Admin and Verifikator only.
      positions: COMPLAINT_READER_POSITIONS,
      icon: (
        <Icon
          icon="solar:chat-round-dots-bold-duotone"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "announcement",
      permission: "Pengumuman",
      label: <Link to={"/announcement"}>Pengumuman</Link>,
      role: ["Direktorat Peningkatan Kapasitas", "*", "Direktorat Vokasi"],
      icon: (
        <Icon
          icon="ic:baseline-announcement"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "faq",
      permission: "FAQ",
      label: <Link to={"/faq"}>FAQ</Link>,
      role: ["Direktorat Peningkatan Kapasitas", "*", "Direktorat Vokasi"],
      icon: isFaqActive ? (
        <FAQEnableIcon
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ) : (
        <FAQIcon
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "testimony",
      permission: "Testimoni",
      label: <Link to={"/testimony"}>Testimoni</Link>,
      role: ["Direktorat Peningkatan Kapasitas", "*", "Direktorat Vokasi"],
      icon: isTestimonyActive ? (
        <TestimonyEnableIcon
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ) : (
        <TestimonyIcon
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "user",
      permission: "User",
      label: <Link to={"/user"}>User</Link>,
      role: ["Direktorat Vokasi", "*", "Direktorat Peningkatan Kapasitas"],
      icon: (
        <Icon
          icon="solar:users-group-two-rounded-bold-duotone"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
    {
      key: "master-data",
      permission: "Master Data",
      label: "Master Data",
      role: ["Direktorat Vokasi", "*", "Direktorat Peningkatan Kapasitas"],
      icon: (
        <Icon
          icon="solar:box-minimalistic-bold-duotone"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
      children: [
        {
          key: "master-data/institutional-type",
          label: (
            <Link to={"/master-data/institutional-type"}>Jenis Lembaga</Link>
          ),
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/vocational-institution-ownership",
          label: (
            <Link to={"/master-data/vocational-institution-ownership"}>
              Kepemilikan Lembaga Vokasi
            </Link>
          ),
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/smk-major",
          label: <Link to={"/master-data/smk-major"}>Jurusan SMK</Link>,
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/education-level",
          label: (
            <Link to={"/master-data/education-level"}>Jenjang Pendidikan</Link>
          ),
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/program-study",
          label: <Link to={"/master-data/program-study"}>Program Studi</Link>,
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/sector",
          label: <Link to={"/master-data/sector"}>Sektoral Pekerjaan</Link>,
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/isco",
          label: <Link to={"/master-data/isco"}>ISCO</Link>,
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/position",
          label: <Link to={"/master-data/position"}>Jabatan Pekerjaan</Link>,
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/governing-ministry",
          label: (
            <Link to={"/master-data/governing-ministry"}>
              Kementerian Pengampu
            </Link>
          ),
          icon: <SubmenuIcon collapse={collapse} />,
        },
        {
          key: "master-data/institutional-affiliation",
          label: (
            <Link to={"/master-data/institutional-affiliation"}>
              Afiliasi / Jejaring Kelembagaan
            </Link>
          ),
          icon: <SubmenuIcon collapse={collapse} />,
        },
      ],
    },
    {
      key: "setting",
      label: "Pengaturan",
      role: ["*"],
      icon: (
        <Icon
          icon="solar:settings-bold-duotone"
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
      children: [
        {
          key: "setting/access",
          label: <Link to={"/setting/access"}>Hak Akses</Link>,
          icon: <SubmenuIcon collapse={collapse} />,
        },
      ],
    },
    {
      key: "history-log",
      permission: "Data Log",
      role: ["Direktorat Vokasi", "*", "Direktorat Peningkatan Kapasitas"],
      label: <Link to={"history-log"}>Data Log</Link>,
      icon: isLogActive ? (
        <LogEnableIcon
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ) : (
        <LogIcon
          style={{ marginBottom: -1, marginLeft: -5 }}
          width="20"
          height="20"
        />
      ),
    },
  ];

  return (
    <>
      {breakpoint.md && (
        <Layout.Sider
          style={{
            paddingTop: 20,
            backgroundColor: "#fff",
            paddingLeft: 4,
            paddingRight: 4,
            borderRight: "1px solid " + pallete.grey[200],
            height: "100%",
          }}
          width={240}
          collapsed={collapse}
        >
          <Flex vertical style={{ height: "100%" }}>
            <Flex justify="center">
              <Image
                src={logo}
                style={{ marginBottom: 20 }}
                width={!collapse ? 150 : 80}
                preview={false}
              />
            </Flex>
            <Menu
              items={groupMenuItems(
                filterMenuItems(items, userDirectorate, userPosition),
                collapse,
              )}
              style={{
                borderRight: "none",
                fontWeight: 400,
                overflowY: "auto",
                flex: 1,
              }}
              defaultSelectedKeys={getSelectedKey()}
              defaultOpenKeys={getOpenKey()}
              mode="inline"
            />
          </Flex>
        </Layout.Sider>
      )}
      <Drawer
        open={drawerCollapse}
        placement="left"
        closable={false}
        width={280}
        styles={{
          body: {
            padding: "14px 6px",
          },
        }}
        onClose={toggleDrawer}
      >
        <Flex vertical style={{ height: "100%" }}>
          <Flex justify="center">
            <Image
              src={logo}
              style={{ marginBottom: 20 }}
              width={!collapse ? 150 : 45}
              preview={false}
            />
          </Flex>
          <Menu
            items={groupMenuItems(
              filterMenuItems(items, userDirectorate, userPosition),
              false,
            )}
            style={{
              borderRight: "none",
              fontWeight: 400,
              overflowY: "auto",
              flex: 1,
            }}
            defaultSelectedKeys={getSelectedKey()}
            defaultOpenKeys={getOpenKey()}
            mode="inline"
            onClick={toggleDrawer}
          />
        </Flex>
      </Drawer>
    </>
  );
}
